import { decimalToPercent } from '@calculations/utilities/decimal-places';
import { createSelector } from '@reduxjs/toolkit';
import { Decimal } from '@snap-types/types/decimal';
import { selectDecimalPlaces } from '@ui/features/quote-builder/state/selectors/decimal-places';
import { selectAllIssues } from '@ui/features/quote-builder/state/selectors/issue';
import { selectQuotePrice } from '@ui/features/quote-builder/state/selectors/quote-price';
import { RootState } from '@ui/store';
import { includes } from 'lodash-es';

import {
  QuotePriceDynamicType,
  QuotePriceEditableType,
} from '../../types/state/quote-price';
import { buildElementId } from '../utilities/element-id';
import { getIssues } from '../utilities/selector-guards';

type FieldName = keyof (QuotePriceDynamicType & QuotePriceEditableType);

const FIXED_FORMAT_FIELDS = [
  'burdenCostAmount',
  'fabricatedMaterialAmount',
  'laborCostAmount',
  'purchaseMaterialAmount',
  'serviceCostAmount',
  'totalPrice',
  'unitCost',
  'unitVariableCosts',
  'unitPrice',
  'userUnitPrice',
  'baseUnitPrice',
  'priceAdjustmentAmount',
] as const satisfies Array<FieldName>;

const PERCENT_FIELDS = [
  'grossMargin',
  'baseGrossMargin',
  'contributionMargin',
] as const satisfies Array<FieldName>;

export const selectQuotePriceField = createSelector(
  [
    selectAllIssues,
    selectDecimalPlaces,
    (
      state: RootState,
      quoteLineUuid: string,
      quotePriceUuid: string,
      field: FieldName,
    ) => selectQuotePrice(state, quoteLineUuid, quotePriceUuid)[field],
    (_, quoteLineUuid: string) => quoteLineUuid,
    (_, __, quotePriceUuid: string) => quotePriceUuid,
    (_, __, ___, field: FieldName) => field,
  ],
  (issues, decimalPlaces, value, quoteLineUuid, quotePriceUuid, field) => {
    const elementId = buildElementId({
      uuid: quotePriceUuid,
      field,
    });

    const fieldIssues = getIssues(issues, {
      area: 'quotePrice',
      quoteLineUuid,
      quotePriceUuid,
      field,
    });

    if (field !== 'changesUnitPrice' && includes(PERCENT_FIELDS, field)) {
      const margin = new Decimal((value as Decimal) ?? 0);

      return {
        elementId,
        value: decimalToPercent(margin, decimalPlaces),
        issues: fieldIssues,
      };
    }

    if (includes(FIXED_FORMAT_FIELDS, field)) {
      return {
        elementId,
        value: (value as Decimal)?.toFixed(decimalPlaces) ?? '',
        issues: fieldIssues,
      };
    }

    return {
      elementId,
      value: value?.toString() ?? '',
      issues: fieldIssues,
    };
  },
);
